import React, { useEffect, useState } from "react";
import { useMobile } from "../hooks/custom/useMobile";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import Carousel from "bootstrap/js/dist/carousel";
import { IAdsData } from "../exports/interfaces";
import { Link } from "react-router-dom";

const Ads = ({ ads }: { ads: IAdsData[] }) => {
  const mobile = useMobile(480);
  const [contentLoaded, setContentLoaded] = useState<boolean[]>([]);
  const [slideLoading, setSlideLoading] = useState(true);
  const videoRefs = React.useRef<(HTMLVideoElement | null)[]>([]);
  const iframeRefs = React.useRef<(HTMLIFrameElement | null)[]>([]);
  const carouselRef = React.useRef<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setContentLoaded(Array(ads.length).fill(false));
  }, [ads.length]);

  useEffect(() => {
    setSlideLoading(!contentLoaded[activeIndex]);
  }, [activeIndex, contentLoaded]);

  const markContentLoaded = (index: number) => {
    setContentLoaded((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  useEffect(() => {
    // Use a session cookie so the flag is shared across tabs but cleared when the browser session ends
    const hasOpenedCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("offcanvas-opened="));

    if (!hasOpenedCookie) {
      const el = document.getElementById("offcanvasRight");

      if (el) {
        const offcanvas = new Offcanvas(el);
        offcanvas.show();

        // Set a session cookie (no expires) shared across tabs for same browser session
        document.cookie = "offcanvas-opened=true; path=/";
      }
    }
  }, []);

  const stopAllMedia = () => {
    // pause videos
    videoRefs.current.forEach((v) => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });

    // reset iframes (reload trick)
    iframeRefs.current.forEach((f) => {
      if (f) {
        const src = f.src;
        f.src = src; // reload stops playback
      }
    });
  };

  useEffect(() => {
    const el = document.getElementById("offcanvasRight");
    if (!el) return;

    const handleClose = () => stopAllMedia();

    el.addEventListener("hidden.bs.offcanvas", handleClose);

    return () => {
      el.removeEventListener("hidden.bs.offcanvas", handleClose);
    };
  }, []);

  useEffect(() => {
    const el = document.getElementById("flyerCarousel");
    if (!el) return;

    const handleSlide = () => stopAllMedia();

    el.addEventListener("slide.bs.carousel", handleSlide);

    return () => {
      el.removeEventListener("slide.bs.carousel", handleSlide);
    };
  }, []);

  useEffect(() => {
    const carouselEl = document.getElementById("flyerCarousel");
    let startY = 0;
    let isTouching = false;

    let swipeCaptured = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      isTouching = true;
      swipeCaptured = false;
      startY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isTouching) return;
      const currentY = e.touches[0].clientY;
      const diff = currentY - startY;

      if (Math.abs(diff) > 10) {
        swipeCaptured = true;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isTouching) return;
      isTouching = false;
      const endY = e.changedTouches[0].clientY;
      const diff = endY - startY;

      if (swipeCaptured && Math.abs(diff) > 40) {
        e.preventDefault();
        e.stopPropagation();
        if (diff < 0) {
          carouselRef.current?.next();
        } else {
          carouselRef.current?.prev();
        }
      }
    };

    if (carouselEl) {
      carouselRef.current = new Carousel(carouselEl, {
        interval: false,
        touch: false,
        ride: false,
      });

      carouselEl.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      carouselEl.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      carouselEl.addEventListener("touchend", handleTouchEnd, {
        passive: false,
      });
    }

    return () => {
      if (carouselEl) {
        carouselEl.removeEventListener("touchstart", handleTouchStart);
        carouselEl.removeEventListener("touchmove", handleTouchMove);
        carouselEl.removeEventListener("touchend", handleTouchEnd);
      }
      if (carouselRef.current) {
        carouselRef.current.dispose();
        carouselRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = document.getElementById("flyerCarousel");
    if (!el) return;

    const handleSlide = (e: any) => {
      const nextIndex = e.to;
      setActiveIndex(nextIndex);
      stopAllMedia();
    };

    el.addEventListener("slide.bs.carousel", handleSlide);

    return () => {
      el.removeEventListener("slide.bs.carousel", handleSlide);
    };
  }, []);

  const normalizeEmbed = (html: string) => {
    if (!html) return { src: "", type: "unknown" };

    // extract iframe src (works even if HTML is broken)
    const srcMatch = html.match(/src=["']([^"']+)["']/i);
    let src = srcMatch?.[1] || "";

    // detect platform
    const isYouTube = src.includes("youtube.com") || src.includes("youtu.be");
    const isFacebook = src.includes("facebook.com");

    // YouTube autoplay fix
    if (isYouTube) {
      const url = new URL(src);
      url.searchParams.set("autoplay", "1");
      url.searchParams.set("mute", "1");
      url.searchParams.set("playsinline", "1");
      src = url.toString();
    }

    // Facebook best-effort autoplay (NOT guaranteed)
    if (isFacebook) {
      if (!src.includes("autoplay")) {
        src += "&autoplay=true";
      }
    }

    // extract width/height
    const widthMatch = html.match(/width=["']?(\d+)["']?/i);
    const heightMatch = html.match(/height=["']?(\d+)["']?/i);

    return {
      src,
      width: widthMatch ? Number(widthMatch[1]) : null,
      height: heightMatch ? Number(heightMatch[1]) : null,
      type: isYouTube ? "youtube" : isFacebook ? "facebook" : "other",
    };
  };

  return (
    <div
      className={`offcanvas ${
        mobile ? "offcanvas-bottom" : "offcanvas-end"
      } offcanvas-flyer`}
      tabIndex={-1}
      id="offcanvasRight"
      aria-labelledby="offcanvasRightLabel"
    >
      <div className="offcanvas-body offcanvas-flyer-body">
        <button
          type="button"
          className="offcanvas-flyer-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        >
          <i className="fa fa-times"></i>
        </button>

        <div className="carousel-wrapper">
          <div
            id="flyerCarousel"
            className="carousel slide h-100"
            style={{ width: "fit-content" }}
          >
            <div className="carousel-inner" style={{ position: "relative" }}>
              <div
                className="carousel-loader-overlay"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: slideLoading ? "flex" : "none",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f4f3ef",
                  zIndex: 5,
                }}
              >
                <div
                  className="spinner-border orange-spinner"
                  role="status"
                ></div>
              </div>
              <div className="carousel-controller">
                <button
                  className="carousel-control-prev"
                  type="button"
                  data-bs-target="#flyerCarousel"
                  data-bs-slide="prev"
                >
                  <span className="carousel-control-prev-icon fs-2"></span>
                </button>
                {/*
                <div className="carousel-indicators">
                  {ads.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      data-bs-target="#flyerCarousel"
                      data-bs-slide-to={index}
                      className={index === 0 ? "active" : ""}
                    ></button>
                  ))}
                </div> */}

                <button
                  className="carousel-control-next"
                  type="button"
                  data-bs-target="#flyerCarousel"
                  data-bs-slide="next"
                >
                  <span className="carousel-control-next-icon"></span>
                </button>
              </div>
              {ads.map((item, index) => {
                const parsed = item.embed_link
                  ? normalizeEmbed(item.embed_link)
                  : null;
                const loaded = contentLoaded[index] ?? false;
                return (
                  <div
                    key={index}
                    className={`carousel-item ${index === 0 ? "active" : ""}`}
                  >
                    {item?.embed_link ? (
                      index === activeIndex && (
                        <div className="embed-wrapper">
                          <div
                            className="iframe-wrapper"
                            style={{
                              width:
                                parsed?.width && parsed?.height
                                  ? `calc(100dvh * ${parsed.width / parsed.height})`
                                  : "56.25dvh",
                              height: "100dvh",
                            }}
                          >
                            <iframe
                              ref={(el) => {
                                iframeRefs.current[index] = el;
                              }}
                              src={parsed?.src}
                              title={`Embed ${index}`}
                              scrolling="no"
                              frameBorder="0"
                              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                              allowFullScreen
                            />

                            <div className="iframe-swipe-zone-vertical iframe-swipe-zone-top" />
                            <div className="iframe-swipe-zone-vertical iframe-swipe-zone-bottom" />
                            <div className="iframe-swipe-zone-horizontal iframe-swipe-zone-right" />
                            <div className="iframe-swipe-zone-horizontal iframe-swipe-zone-left" />
                          </div>
                        </div>
                      )
                    ) : item?.videos && item.videos.length > 0 ? (
                      <div
                        className="video-slide"
                        style={{ position: "relative" }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "#000",
                            opacity: loaded ? 0 : 1,
                            transition: "opacity 0.2s ease-in",
                            zIndex: 0,
                          }}
                        />
                        <video
                          ref={(el) => {
                            videoRefs.current[index] = el;
                          }}
                          className={`mppVideo ${loaded ? "show-video" : "hide-video"}`}
                          autoPlay
                          muted
                          loop
                          playsInline
                          controls
                          onLoadedData={() => markContentLoaded(index)}
                          style={{
                            opacity: loaded ? 1 : 0,
                            transition: "opacity 0.2s ease-in",
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_cmsUrl}${item.videos[0]?.media?.url}`}
                            type="video/mp4"
                          />
                        </video>
                      </div>
                    ) : item.link ? (
                      <div
                        className={`${item?.description ? "mppOuterCard" : ""}`}
                      >
                        <div
                          className={`${item?.description ? "mppCard" : ""}`}
                        >
                          <Link
                            to={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={`${process.env.REACT_APP_cmsUrl}${item?.image?.[0]?.media?.url}`}
                              className="mppImg"
                              alt={`Flyer ${index}`}
                              onLoad={() => markContentLoaded(index)}
                              style={
                                mobile
                                  ? {
                                      height: "auto",
                                      width: "100dvw",
                                      objectFit: "contain",
                                    }
                                  : {
                                      height: "100dvh",
                                      width: "auto",
                                      objectFit: "contain",
                                    }
                              }
                            />
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`${item?.description ? "mppOuterCard" : ""}`}
                      >
                        <div
                          className={`${item?.description ? "mppCard" : ""}`}
                        >
                          <img
                            src={`${process.env.REACT_APP_cmsUrl}${item?.image?.[0]?.media?.url}`}
                            className="mppImg"
                            alt={`Flyer ${index}`}
                            onLoad={() => markContentLoaded(index)}
                            style={
                              mobile
                                ? {
                                    height: "auto",
                                    width: "100dvw",
                                    objectFit: "contain",
                                  }
                                : {
                                    height: "100dvh",
                                    width: "auto",
                                    objectFit: "contain",
                                  }
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ads;
